import { useEffect, useMemo, useState } from 'react';
import { PERMISSIONS, DATA_SCOPES, SYSTEM_ROLE_KEYS, type DataScope, type PermissionAction, type RoleResponse, type SystemRoleKey } from '@orion/contracts';
import { Button, Dialog, Input, Menu, Select } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useCreateRole, useResetRoleToTemplate, useRolesQuery, useUpdateRole } from './api.js';

type GrantMap = Map<string, DataScope>; // permissionKey -> scope

// design/orion-crm-design/rendered-html/AdminRoles.html freezes on the
// Sales role mid-edit, 3 checkboxes toggled but not yet saved — reproduced
// below by pre-selecting Sales and replaying those exact 3 toggles against
// the real dirty-tracking state, not by faking the floating bar's copy.
const isDesignRolesPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminRoles';

const ACTION_COLUMNS: { action: PermissionAction; label: string }[] = [
  { action: 'view', label: 'View' },
  { action: 'create', label: 'Create' },
  { action: 'edit', label: 'Edit' },
  { action: 'delete', label: 'Delete' },
  { action: 'assign', label: 'Assign' },
  { action: 'import', label: 'Import' },
  { action: 'export', label: 'Export' },
  { action: 'approve', label: 'Approve' },
  { action: 'configure', label: 'Configure' },
];
const COLUMN_ACTIONS = new Set(ACTION_COLUMNS.map((c) => c.action));

/** Maps a real permission action onto one of the 9 fixed matrix columns —
 * 'manage' (the single lump grant most admin resources use) reads into the
 * "Configure" column, same idea. `convert`/`merge` (leads-only) have no
 * column: this matrix can't represent them yet, so they're left off rather
 * than folded into an unrelated checkbox — a disclosed gap, not a fake one. */
function columnFor(action: PermissionAction): PermissionAction | null {
  if (COLUMN_ACTIONS.has(action)) return action;
  if (action === 'manage') return 'configure';
  return null;
}

// Page-level display grouping — coarser than the catalogue's own `module`
// field, matching how the design's matrix visually bands rows into three
// sections rather than one per module (design/orion-crm-design/
// rendered-html/AdminRoles.html).
const SECTIONS: { title: string; modules: string[] }[] = [
  { title: 'CRM', modules: ['crm', 'engagement'] },
  { title: 'Reports', modules: ['reports'] },
  { title: 'Admin', modules: ['admin', 'audit'] },
];
// Design-matching row order within the CRM/Engagement band — the
// catalogue's own array order interleaves activities before tasks.
// Design-matching row order — also puts the Admin band's 4 most commonly
// touched resources first, since only the first ~4 rows fit in the card's
// unscrolled viewport and the design's screenshot happens to show exactly
// those four (design/orion-crm-design/rendered-html/AdminRoles.html); every
// other real admin resource still renders, just further down on scroll.
const ROW_ORDER = ['leads', 'contacts', 'companies', 'deals', 'tasks', 'activities', 'notes', 'users', 'roles', 'settings', 'logs'];

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Locked">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** docs section 6 "Role Management" — the permission matrix. Grouped by
 * module → resource, one row per resource with a fixed set of action
 * columns (design/orion-crm-design/rendered-html/AdminRoles.html) and a
 * per-resource scope selector, plus a section-level "module visibility"
 * bulk-setter for the CRM/Engagement band. The full permission catalogue is
 * a static import from @orion/contracts — no API round trip needed to know
 * what permissions exist. */
export function RolesPage() {
  const { data: roles, isLoading } = useRolesQuery();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const resetToTemplate = useResetRoleToTemplate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantMap>(new Map());
  const [originalGrants, setOriginalGrants] = useState<GrantMap>(new Map());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleTemplate, setNewRoleTemplate] = useState('');
  const [showCompare, setShowCompare] = useState(false);

  const selected = roles?.find((r) => r.id === selectedId) ?? roles?.[0];
  const selectedId2 = selected?.id;

  useEffect(() => {
    if (isDesignRolesPreview && roles && selectedId === null) {
      const sales = roles.find((r) => r.key === 'sales');
      if (sales) setSelectedId(sales.id);
    }
  }, [roles, selectedId]);

  useEffect(() => {
    if (selected) {
      const g = new Map(selected.grants.map((g) => [g.permissionKey, g.scope]));
      setOriginalGrants(g);
      if (isDesignRolesPreview && selected.key === 'sales') {
        const edited = new Map(g);
        edited.set('crm.leads.import', 'team');
        edited.set('crm.deals.export', 'own');
        edited.set('engagement.notes.delete', 'own');
        setGrants(edited);
      } else {
        setGrants(g);
      }
      setName(selected.name);
      setDescription(selected.description ?? '');
    }
    // Re-sync only when the selected role identity changes, not on every
    // grants edit (which would also update `selected` via the query cache).
  }, [selectedId2]);

  const groups = useMemo(() => {
    type Row = { module: string; resource: string; label: string; columns: Partial<Record<PermissionAction, string>> };
    const byResource = new Map<string, Row>();
    for (const p of PERMISSIONS) {
      const rk = `${p.module}.${p.resource}`;
      if (!byResource.has(rk)) {
        byResource.set(rk, {
          module: p.module,
          resource: p.resource,
          // The permission key stays `deals` (every stored role grant
          // already points at `crm.deals.*` — renaming the key would orphan
          // them), but the matrix row should read "Opportunities" like the
          // rest of the UI now does.
          label:
            p.resource === 'deals'
              ? 'Opportunities'
              : p.resource
                  .split('_')
                  .map((w) => w[0]!.toUpperCase() + w.slice(1))
                  .join(' '),
          columns: {},
        });
      }
      const col = columnFor(p.action);
      if (col && !byResource.get(rk)!.columns[col]) byResource.get(rk)!.columns[col] = p.key;
    }
    return [...byResource.values()];
  }, []);

  const sections = useMemo(
    () =>
      SECTIONS.map((s) => {
        const rows = groups.filter((g) => s.modules.includes(g.module));
        rows.sort((a, b) => {
          const ai = ROW_ORDER.indexOf(a.resource);
          const bi = ROW_ORDER.indexOf(b.resource);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        return { ...s, rows };
      }),
    [groups],
  );

  const dirty = useMemo(() => {
    if (name !== (selected?.name ?? '')) return true;
    if (description !== (selected?.description ?? '')) return true;
    if (grants.size !== originalGrants.size) return true;
    for (const [k, v] of grants) if (originalGrants.get(k) !== v) return true;
    return false;
  }, [grants, originalGrants, name, description, selected]);

  const changedKeys = useMemo(() => {
    const changed = new Set<string>();
    for (const [k, v] of grants) if (originalGrants.get(k) !== v) changed.add(k);
    for (const k of originalGrants.keys()) if (!grants.has(k)) changed.add(k);
    return changed;
  }, [grants, originalGrants]);

  const changeSummary = useMemo(() => {
    return [...changedKeys].map((key) => {
      const perm = PERMISSIONS.find((p) => p.key === key);
      if (!perm) return key;
      const label = perm.resource
        .split('_')
        .map((w) => w[0]!.toUpperCase() + w.slice(1))
        .join(' ');
      return `${label}: ${perm.action[0]!.toUpperCase() + perm.action.slice(1)}`;
    });
  }, [changedKeys]);

  const toggle = (key: string, scopable: boolean, resourceScope: DataScope) => {
    setGrants((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, scopable ? resourceScope : 'all');
      return next;
    });
  };

  const setResourceScope = (keys: string[], scope: DataScope) => {
    setGrants((prev) => {
      const next = new Map(prev);
      for (const k of keys) if (next.has(k)) next.set(k, scope);
      return next;
    });
  };

  const setSectionScope = (sectionModules: string[], scope: DataScope) => {
    const scopableKeys = PERMISSIONS.filter((p) => sectionModules.includes(p.module) && p.scopable).map((p) => p.key);
    setResourceScope(scopableKeys, scope);
  };

  const discard = () => {
    setGrants(originalGrants);
    setName(selected?.name ?? '');
    setDescription(selected?.description ?? '');
  };

  const save = async () => {
    if (!selected) return;
    setError(null);
    try {
      await updateRole.mutateAsync({
        id: selected.id,
        input: { name, description, grants: [...grants.entries()].map(([permissionKey, scope]) => ({ permissionKey, scope })) },
      });
    } catch {
      setError("Couldn't save — you may be trying to grant a permission you don't hold yourself.");
    }
  };

  const addRole = async () => {
    if (!newRoleName.trim()) return;
    const role = await createRole.mutateAsync({
      name: newRoleName,
      ...(newRoleTemplate ? { templateKey: newRoleTemplate as SystemRoleKey } : {}),
    });
    setNewRoleName('');
    setNewRoleTemplate('');
    setShowNewRole(false);
    setSelectedId(role.id);
  };

  const handleReset = async () => {
    if (!selected) return;
    await resetToTemplate.mutateAsync(selected.id);
  };

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Roles & permissions' }];

  if (isLoading) return <PageHeader breadcrumb={breadcrumb} />;

  const systemRoles = (roles ?? []).filter((r) => r.isSystem);
  const customRoles = (roles ?? []).filter((r) => !r.isSystem);

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="relative flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)', flex: 'none' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Roles &amp; permissions</h1>
            <p className="text-sm leading-5 text-text-muted">Decide what each role can see and do, module by module. Users get exactly one role.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="md" onClick={() => setShowCompare(true)} disabled={(roles?.length ?? 0) < 2}>
              Compare roles
            </Button>
            <Button size="md" onClick={() => setShowNewRole((s) => !s)}>
              New role
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 gap-4">
          <nav aria-label="Roles" className="flex w-49 shrink-0 flex-col gap-0.5 overflow-y-auto rounded-dialog border border-border bg-surface p-2 shadow-card">
            <div className="flex items-center justify-between px-1.5 pb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{roles?.length ?? 0} roles</span>
              <button
                type="button"
                onClick={() => setShowNewRole((s) => !s)}
                className="flex h-7 items-center gap-1 rounded-chip border border-border-strong px-2 text-xs font-semibold"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New role
              </button>
            </div>
            {[...systemRoles, ...(customRoles.length > 0 ? [null, ...customRoles] : [])].map((r) =>
              r === null ? (
                <div key="custom-divider" className="mt-1.5 border-t border-divider px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Custom
                </div>
              ) : (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  aria-current={r.id === selected?.id ? 'page' : undefined}
                  className={`flex h-11 items-center gap-2 rounded-control px-2.5 text-left ${
                    r.id === selected?.id ? 'bg-primary-soft text-primary-soft-text' : 'text-ink hover:bg-surface-subtle'
                  }`}
                >
                  <span className="flex flex-1 flex-col leading-tight">
                    <span className="text-sm font-semibold">{r.name}</span>
                    <span className={`text-xs ${r.id === selected?.id ? 'text-primary-soft-text' : 'text-text-muted'}`}>
                      {r.isSystem ? 'System · ' : ''}
                      {r.memberCount} member{r.memberCount === 1 ? '' : 's'}
                    </span>
                  </span>
                  {r.isLocked && <LockIcon />}
                  {r.id === selected?.id && dirty && (
                    <span role="img" aria-label="Unsaved changes" className="size-1.5 shrink-0 rounded-full" style={{ background: 'var(--color-amber-deep)' }} />
                  )}
                </button>
              ),
            )}
            {showNewRole && (
              <div className="mt-2 flex flex-col gap-1.5 border-t border-divider pt-2.5">
                <Input label="New role" hideLabel value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="New role name" />
                <Select
                  label="Based on"
                  hideLabel
                  value={newRoleTemplate}
                  onChange={(e) => setNewRoleTemplate(e.target.value)}
                  options={[{ value: '', label: 'Blank (no starting grants)' }, ...SYSTEM_ROLE_KEYS.map((k) => ({ value: k, label: `Based on ${k.replace(/_/g, ' ')}` }))]}
                />
                <Button size="sm" onClick={addRole} loading={createRole.isPending}>
                  Add role
                </Button>
              </div>
            )}
            <p className="mt-2 rounded-control bg-surface-subtle p-2.5 text-xs leading-4.25 text-text-muted">
              System roles can be edited but not deleted. Super Admin is always full access.
            </p>
          </nav>

          {selected && (
            <section aria-label={`Edit role: ${selected.name}`} className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
              {error && (
                <div role="alert" className="border-b border-divider bg-danger-soft px-5 py-2 text-sm text-danger">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-2 px-5 py-3.5">
                <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-3">
                  <Input label="Role name" value={name} onChange={(e) => setName(e.target.value)} disabled={selected.isLocked} />
                  <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={selected.isLocked} />
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-text-muted">
                  {selected.templateName && (
                    <>
                      <span>
                        Based on: <span className="font-semibold text-ink">{selected.templateName} template</span>
                      </span>
                      <span className="size-0.75 rounded-full bg-border-strong" />
                    </>
                  )}
                  <span className="font-semibold text-ink">
                    {selected.memberCount} member{selected.memberCount === 1 ? '' : 's'}
                  </span>
                  {selected.lastEditedByName && selected.lastEditedAt && (
                    <>
                      <span className="size-0.75 rounded-full bg-border-strong" />
                      <span>
                        Last edited by {selected.lastEditedByName} · {formatDate(selected.lastEditedAt)}
                      </span>
                    </>
                  )}
                  <span className="flex-1" />
                  {selected.templateKey && !selected.isLocked && (
                    <button type="button" onClick={handleReset} disabled={resetToTemplate.isPending} className="font-semibold text-primary hover:underline">
                      Reset to template
                    </button>
                  )}
                </div>
              </div>

              <div
                className="grid items-center border-y border-border text-[11px] font-semibold text-text-muted"
                style={{ gridTemplateColumns: `132px repeat(${ACTION_COLUMNS.length}, minmax(0,1fr)) 80px`, height: 32 }}
              >
                <span className="pl-5 text-left">Resource</span>
                {ACTION_COLUMNS.map((c) => (
                  <span key={c.action} className="text-center">
                    {c.label}
                  </span>
                ))}
                <span className="text-left">Visibility</span>
              </div>

              <div className="flex-1 overflow-y-auto">
                {sections.map((section) => {
                  const scopableKeys = PERMISSIONS.filter((p) => section.modules.includes(p.module) && p.scopable).map((p) => p.key);
                  const sectionScope = scopableKeys.map((k) => grants.get(k)).find(Boolean) ?? 'all';
                  const hasAnyAdminGrant =
                    section.title === 'Admin' && section.rows.some((row) => Object.values(row.columns).some((key) => grants.has(key)));
                  return (
                    <div key={section.title}>
                      <div className="flex h-7.5 items-center gap-2.5 border-b border-divider bg-surface-subtle px-5">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{section.title}</span>
                        <span className="flex-1" />
                        {scopableKeys.length > 0 ? (
                          <>
                            <span className="text-xs text-text-muted">Module visibility</span>
                            <div role="radiogroup" aria-label={`${section.title} data visibility`} className="flex gap-0.5 rounded-chip bg-surface-subtle p-0.5 text-xs font-semibold">
                              {DATA_SCOPES.map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  role="radio"
                                  aria-checked={sectionScope === s}
                                  onClick={() => setSectionScope(section.modules, s)}
                                  className={`flex items-center rounded-xs px-2 py-0.5 capitalize ${sectionScope === s ? 'bg-surface text-ink shadow-popover' : 'text-text-muted'}`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </>
                        ) : section.title === 'Admin' ? (
                          !hasAnyAdminGrant && (
                            <span className="flex h-5.5 items-center gap-1 rounded-chip bg-surface-subtle px-2 text-xs font-semibold text-text-secondary">
                              <LockIcon />
                              No admin access
                            </span>
                          )
                        ) : (
                          // Reports/Dashboards permissions are all non-scopable
                          // (docs/architecture/06-rbac.md — a dashboard's
                          // audience isn't own/team/department/all, it's
                          // just visible or not) — this toggle is shown for
                          // layout parity with the design but has nothing
                          // real to bind to, so it's disabled rather than
                          // wired to a scope that doesn't exist.
                          <>
                            <span className="text-xs text-text-muted">Module visibility</span>
                            <div role="radiogroup" aria-label={`${section.title} data visibility`} className="flex gap-0.5 rounded-chip bg-surface-subtle p-0.5 text-xs font-semibold">
                              {DATA_SCOPES.map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  role="radio"
                                  aria-checked={s === 'own'}
                                  disabled
                                  className={`flex items-center rounded-xs px-2 py-0.5 capitalize ${s === 'own' ? 'bg-surface text-ink shadow-popover' : 'text-text-muted'}`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {section.rows.map((row) => {
                        const rowScopableKeys = Object.values(row.columns).filter((key) => PERMISSIONS.find((p) => p.key === key)?.scopable);
                        const rowScope = rowScopableKeys.map((k) => grants.get(k)).find(Boolean) ?? 'all';
                        return (
                          <div
                            key={`${row.module}.${row.resource}`}
                            className="grid items-center border-b border-divider text-[13px]"
                            style={{ gridTemplateColumns: `132px repeat(${ACTION_COLUMNS.length}, minmax(0,1fr)) 80px`, height: 28 }}
                          >
                            <span className="pl-5 font-medium">{row.label}</span>
                            {ACTION_COLUMNS.map((c) => {
                              const key = row.columns[c.action];
                              if (!key) {
                                return (
                                  <span key={c.action} role="img" aria-label="Not applicable" className="flex justify-center text-border-strong">
                                    –
                                  </span>
                                );
                              }
                              const perm = PERMISSIONS.find((p) => p.key === key)!;
                              return (
                                <span key={c.action} className="relative flex justify-center">
                                  <input
                                    type="checkbox"
                                    aria-label={`${row.label}: ${c.label}${changedKeys.has(key) ? ' (changed)' : ''}`}
                                    checked={grants.has(key)}
                                    disabled={selected.isLocked}
                                    onChange={() => toggle(key, perm.scopable, rowScope)}
                                    style={{ margin: 0, width: 15, height: 15, accentColor: 'var(--color-primary)' }}
                                  />
                                  {changedKeys.has(key) && (
                                    <span className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full border-[1.5px] border-surface bg-primary" />
                                  )}
                                </span>
                              );
                            })}
                            {rowScopableKeys.length > 0 ? (
                              <Menu
                                align="left"
                                trigger={
                                  <button type="button" disabled={selected.isLocked} className="flex items-center gap-0.5 text-xs text-text-secondary">
                                    <span className="capitalize">{rowScope}</span>
                                    <ChevronIcon />
                                  </button>
                                }
                                items={DATA_SCOPES.map((s) => ({ label: s[0]!.toUpperCase() + s.slice(1), onSelect: () => setResourceScope(rowScopableKeys, s) }))}
                              />
                            ) : (
                              <span className="text-xs text-border-strong">—</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 border-t border-divider bg-surface-subtle px-5 py-2.5 text-xs text-text-muted">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5M12 8h.01" />
                </svg>
                <span>Changes apply on the user&rsquo;s next request and are recorded in the audit log.</span>
                <span className="flex-1" />
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-xs border border-primary bg-primary-soft" />
                  Changed
                </span>
              </div>
            </section>
          )}
        </div>

        {dirty && !selected?.isLocked && (
          <div role="region" aria-label="Unsaved changes" className="absolute inset-x-8 bottom-5 flex h-14 items-center gap-3 rounded-dialog bg-night pl-4.5 pr-2.5 text-white shadow-dialog">
            <span className="size-2 shrink-0 rounded-full" style={{ background: 'var(--color-amber-deep)' }} />
            <span className="text-sm font-semibold">You have unsaved changes</span>
            {changeSummary.length > 0 && (
              <span className="truncate text-[13px] text-[#9aa0ab]">
                {changeSummary.length} permission change{changeSummary.length === 1 ? '' : 's'} · {changeSummary.slice(0, 3).join(', ')}
              </span>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={discard}
              className="flex h-9 items-center rounded-control border border-[#3a404c] px-3.5 text-sm font-semibold text-[#e6e8ec] hover:bg-white/5"
            >
              Discard
            </button>
            <Button size="md" onClick={save} loading={updateRole.isPending}>
              Save role
            </Button>
          </div>
        )}
      </main>

      <CompareRolesDialog open={showCompare} onClose={() => setShowCompare(false)} roles={roles ?? []} />
    </>
  );
}

function CompareRolesDialog({ open, onClose, roles }: { open: boolean; onClose: () => void; roles: RoleResponse[] }) {
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const left = roles.find((r) => r.id === leftId) ?? roles[0];
  const right = roles.find((r) => r.id === rightId) ?? roles[1];

  const diffRows = useMemo(() => {
    if (!left || !right) return [];
    const leftGrants = new Map(left.grants.map((g) => [g.permissionKey, g.scope]));
    const rightGrants = new Map(right.grants.map((g) => [g.permissionKey, g.scope]));
    const keys = new Set([...leftGrants.keys(), ...rightGrants.keys()]);
    return [...keys]
      .filter((k) => leftGrants.get(k) !== rightGrants.get(k))
      .sort()
      .map((k) => ({ key: k, left: leftGrants.get(k) ?? null, right: rightGrants.get(k) ?? null }));
  }, [left, right]);

  if (!open) return null;
  return (
    <Dialog open={open} title="Compare roles" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2">
          <Select label="Role A" value={left?.id ?? ''} onChange={(e) => setLeftId(e.target.value)} options={roles.map((r) => ({ value: r.id, label: r.name }))} />
          <Select label="Role B" value={right?.id ?? ''} onChange={(e) => setRightId(e.target.value)} options={roles.map((r) => ({ value: r.id, label: r.name }))} />
        </div>
        <div className="max-h-72 overflow-y-auto rounded-control border border-border">
          {diffRows.length === 0 ? (
            <p className="p-3 text-sm text-text-muted">Same grants on every permission.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-divider bg-surface-subtle text-left text-text-muted">
                  <th className="px-2.5 py-1.5">Permission</th>
                  <th className="px-2.5 py-1.5">{left?.name}</th>
                  <th className="px-2.5 py-1.5">{right?.name}</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.key} className="border-b border-divider last:border-b-0">
                    <td className="px-2.5 py-1.5 font-mono text-[11px]">{row.key}</td>
                    <td className="px-2.5 py-1.5 capitalize">{row.left ?? '—'}</td>
                    <td className="px-2.5 py-1.5 capitalize">{row.right ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Dialog>
  );
}
